import { useCallback, useMemo, useRef, type MutableRefObject } from 'react'
import type {
  BotProtectionWidgetInstance,
  BotProtectionWidgetProps,
  BotProtectionWidgetReason,
} from './types'
import type { BotProtectionRuntimeEventLogger } from './runtimeEventLogger'
import type { BotProtectionRuntimeStateController } from './widgetRuntimeState'

const DEFAULT_MAX_RETRIES = 2
const DEFAULT_RETRY_DELAY_MS = 350
const DEFAULT_FAILURE_DEDUP_WINDOW_MS = 240

const isFatalChallengeError = (errorCode?: string): boolean => {
  if (!errorCode) return false
  return errorCode.startsWith('1102')
}

type BotProtectionRetryPolicyInput = {
  lifecycle: BotProtectionWidgetProps['lifecycle']
  logRuntimeEvent: BotProtectionRuntimeEventLogger
  runtimeState: Pick<BotProtectionRuntimeStateController, 'setRuntimeState'>
  widgetRef: MutableRefObject<BotProtectionWidgetInstance | null>
}

export type BotProtectionRetryPolicy = {
  clearRetryTimer: () => void
  resetAttempts: () => void
  scheduleReset: (reason: BotProtectionWidgetReason, errorCode?: string) => void
}

export const useBotProtectionRetryPolicy = ({
  lifecycle,
  logRuntimeEvent,
  runtimeState,
  widgetRef,
}: BotProtectionRetryPolicyInput): BotProtectionRetryPolicy => {
  const retryAttemptsRef = useRef(0)
  const retryTimerRef = useRef<number | null>(null)
  const lastFailureTsRef = useRef(0)

  const maxRetries = lifecycle?.maxRetries ?? DEFAULT_MAX_RETRIES
  const retryDelayMs = lifecycle?.retryDelayMs ?? DEFAULT_RETRY_DELAY_MS
  const failureDedupWindowMs =
    lifecycle?.failureDedupWindowMs ?? DEFAULT_FAILURE_DEDUP_WINDOW_MS

  const clearRetryTimer = useCallback(() => {
    if (!retryTimerRef.current) return
    window.clearTimeout(retryTimerRef.current)
    retryTimerRef.current = null
  }, [])

  const resetAttempts = useCallback(() => {
    retryAttemptsRef.current = 0
  }, [])

  const scheduleReset = useCallback(
    (reason: BotProtectionWidgetReason, errorCode?: string) => {
      if (!widgetRef.current) return

      if (reason === 'error' && isFatalChallengeError(errorCode)) {
        runtimeState.setRuntimeState('failed', { reason, errorCode })
        logRuntimeEvent('warn', 'bot_protection.retry_not_scheduled_fatal_error', {
          reason,
          errorCode,
        })
        return
      }

      const now = Date.now()
      if (now - lastFailureTsRef.current <= failureDedupWindowMs) {
        return
      }
      lastFailureTsRef.current = now

      if (reason !== 'expired') {
        if (retryAttemptsRef.current >= maxRetries) {
          logRuntimeEvent('warn', 'bot_protection.retry_exhausted', {
            reason,
            maxRetries,
          })
          return
        }
        retryAttemptsRef.current += 1
      }

      clearRetryTimer()
      retryTimerRef.current = window.setTimeout(() => {
        if (!widgetRef.current) return
        widgetRef.current.reset()
        runtimeState.setRuntimeState('verifying', { reason })
        logRuntimeEvent('info', 'bot_protection.widget_reset_scheduled', {
          reason,
          retryAttempt: retryAttemptsRef.current,
        })
      }, retryDelayMs)
    },
    [
      clearRetryTimer,
      failureDedupWindowMs,
      logRuntimeEvent,
      maxRetries,
      retryDelayMs,
      runtimeState,
      widgetRef,
    ],
  )

  return useMemo(
    () => ({ clearRetryTimer, resetAttempts, scheduleReset }),
    [clearRetryTimer, resetAttempts, scheduleReset],
  )
}
